import type { InputAction } from '../types';

type Listener = (action: InputAction) => void;

const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable="true"]';
const INTERACTIVE_SELECTOR = `${EDITABLE_SELECTOR}, button, a, label`;

function isEditable(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(EDITABLE_SELECTOR) !== null;
}

function isInteractive(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(INTERACTIVE_SELECTOR) !== null;
}

export class InputManager {
  private readonly listeners = new Set<Listener>();

  constructor(private readonly target: HTMLElement | Document = document) {
    target.addEventListener('keydown', this.handleKeyDown as EventListener);
    target.addEventListener('keyup', this.handleKeyUp as EventListener);
    target.addEventListener('touchstart', this.handleTouch as EventListener, {
      passive: false,
    });
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  destroy(): void {
    this.target.removeEventListener('keydown', this.handleKeyDown as EventListener);
    this.target.removeEventListener('keyup', this.handleKeyUp as EventListener);
    this.target.removeEventListener('touchstart', this.handleTouch as EventListener);
    this.listeners.clear();
  }

  private emit(action: InputAction): void {
    for (const listener of this.listeners) listener(action);
  }

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    // input / textarea 등에서 타이핑 중이면 게임 핸들러 무시 (닉네임 입력 보호)
    if (isEditable(event.target)) return;

    if (event.code === 'Space') {
      event.preventDefault();
      // Space: waiting 시 시작 + running 시 점프 양쪽으로 동작
      this.emit('start');
      this.emit('jump');
    } else if (event.code === 'ArrowUp') {
      event.preventDefault();
      // ↑ 는 점프 전용 — 시작에는 사용 안 함
      this.emit('jump');
    } else if (event.code === 'ArrowDown') {
      event.preventDefault();
      this.emit('duck-start');
    } else if (event.code === 'KeyR') {
      this.emit('restart');
    }
  };

  private readonly handleKeyUp = (event: KeyboardEvent): void => {
    if (isEditable(event.target)) return;
    if (event.code === 'ArrowDown') {
      this.emit('duck-end');
    }
  };

  private readonly handleTouch = (event: TouchEvent): void => {
    // 폼/버튼 등 인터랙티브 요소 터치는 게임 입력으로 흘리지 않음 (제출/재시작 버튼 보호)
    if (isInteractive(event.target)) return;
    event.preventDefault();
    this.emit('start');
    this.emit('jump');
    this.emit('restart');
  };
}
